/**
 * <Watchlist> — 개인 관심종목
 *
 * 사용자가 종목 추가/삭제 (localStorage 영속화).
 * Polygon snapshot bulk 로 가격/등락률 한 번에 조회.
 *
 * 데이터: POST /api/ticker/market/watchlist  body: { symbols: ['AAPL', ...] }
 */
"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Plus, X } from "lucide-react";
import type { WidgetMeta, WidgetProps } from "../types";
import { koreanCompanyName } from "@/lib/korean-company-name";
import { WATCHLIST } from "@/lib/analytics-events";

interface Quote {
  symbol: string;
  name?: string | null;
  price: number | null;
  changePercent: number | null;
  change: number | null;
}

const REFRESH_MS = 60 * 1000;
const LS_KEY = "dashboard:watchlist";
const DEFAULT_SYMBOLS = ["AAPL", "NVDA", "TSLA", "MSFT", "GOOGL"];

function loadSymbols(): string[] {
  if (typeof window === "undefined") return DEFAULT_SYMBOLS;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_SYMBOLS;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length > 0
      ? arr.filter((s) => typeof s === "string").map((s) => s.toUpperCase()).slice(0, 20)
      : DEFAULT_SYMBOLS;
  } catch {
    return DEFAULT_SYMBOLS;
  }
}

function saveSymbols(list: string[]) {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch { /* ignore */ }
}

export function Watchlist(_props: WidgetProps) {
  const [symbols, setSymbols] = useState<string[]>(() => loadSymbols());
  const [quotes, setQuotes] = useState<Record<string, Quote>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newSymbol, setNewSymbol] = useState("");
  const mountedRef = useRef(true);

  useEffect(() => { saveSymbols(symbols); }, [symbols]);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      if (symbols.length === 0) { setQuotes({}); setLoading(false); return; }
      try {
        const q = encodeURIComponent(symbols.join(","));
        const res = await fetch(`/api/ticker/market/watchlist?symbols=${q}`, {
          credentials: "include",
        });
        if (!res.ok) {
          if (mountedRef.current) { setError("로드 실패"); setLoading(false); }
          return;
        }
        const d = await res.json();
        const map: Record<string, Quote> = {};
        for (const item of (d.items || [])) {
          if (item?.symbol) map[item.symbol] = item;
        }
        if (mountedRef.current) {
          setQuotes(map);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) { setError("네트워크 오류"); setLoading(false); }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, [symbols]);

  const handleAdd = () => {
    const sym = newSymbol.trim().toUpperCase();
    if (!sym || symbols.includes(sym) || symbols.length >= 20) {
      setNewSymbol("");
      setAdding(false);
      return;
    }
    setSymbols((prev) => [...prev, sym]);
    WATCHLIST.added(sym);
    setNewSymbol("");
    setAdding(false);
  };

  const handleRemove = (sym: string) => {
    setSymbols((prev) => prev.filter((s) => s !== sym));
    WATCHLIST.removed(sym);
  };

  const fmtPct = (v: number | null) => {
    if (v == null || !Number.isFinite(v)) return "—";
    const sign = v >= 0 ? "+" : "";
    return `${sign}${(v * 100).toFixed(2)}%`;
  };
  const fmtPrice = (v: number | null) => {
    if (v == null || !Number.isFinite(v)) return "—";
    return v.toFixed(2);
  };

  return (
    <div className="dw-body dw-watchlist">
      <div className="dw-head">
        <span className="dw-title">관심종목</span>
        <button
          type="button"
          className="dw-action-btn"
          onClick={() => setAdding((a) => !a)}
          disabled={symbols.length >= 20}
          title={symbols.length >= 20 ? "최대 20개" : "종목 추가"}
          aria-label="종목 추가"
        >
          <Plus size={14} />
        </button>
      </div>

      {adding && (
        <div className="dw-watchlist-add">
          <input
            type="text"
            className="dw-watchlist-input"
            placeholder="심볼 입력 (예: AAPL)"
            value={newSymbol}
            onChange={(e) => setNewSymbol(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
              if (e.key === "Escape") { setAdding(false); setNewSymbol(""); }
            }}
            autoFocus
            spellCheck={false}
          />
          <button type="button" className="dw-action-btn" onClick={handleAdd}>추가</button>
        </div>
      )}

      {error ? (
        <div className="dw-watchlist-error">{error}</div>
      ) : (
        <div className="dw-watchlist-list">
          {loading && Object.keys(quotes).length === 0 ? (
            <div className="dw-watchlist-empty">로딩 중…</div>
          ) : symbols.length === 0 ? (
            <div className="dw-watchlist-empty">종목을 추가해주세요</div>
          ) : (
            symbols.map((sym) => {
              const q = quotes[sym];
              const pct = q?.changePercent ?? null;
              const up = pct != null && pct > 0;
              const down = pct != null && pct < 0;
              return (
                <div key={sym} className="dw-watchlist-row">
                  <Link
                    href={`/analyze/${encodeURIComponent(sym)}`}
                    className="dw-watchlist-link"
                    title={`${sym} · ${koreanCompanyName(sym, q?.name)}`}
                  >
                    <span className="dw-watchlist-name-block">
                      <span className="dw-watchlist-sym">{koreanCompanyName(sym, q?.name)}</span>
                      <span className="dw-watchlist-ticker">{sym}</span>
                    </span>
                    {/* 사용자 결정 #6: price + pct 한 셀 (이름 클램핑 방지) */}
                    <span className="dw-watchlist-pricepct">
                      <span className="dw-watchlist-price">{fmtPrice(q?.price ?? null)}</span>
                      <span className={`dw-watchlist-pct ${up ? "up" : down ? "down" : ""}`}>
                        {fmtPct(pct)}
                      </span>
                    </span>
                  </Link>
                  <button
                    type="button"
                    className="dw-watchlist-rm"
                    onClick={() => handleRemove(sym)}
                    aria-label={`${sym} 삭제`}
                    title="삭제"
                  >
                    <X size={11} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export const watchlistMeta: WidgetMeta = {
  type: "watchlist",
  name: "관심종목",
  description: "개인 종목 추가/삭제 · 실시간 가격 · 1분 자동 새로고침",
  icon: "⭐",
  category: "personal",
  defaultSize: { w: 4, h: 6 },
  availableSizes: [
    { w: 3, h: 5 },
    { w: 4, h: 6 },
    { w: 4, h: 8 },
    { w: 6, h: 6 },
  ],
  minW: 3,
  maxW: 6,
  minH: 4,
  maxH: 10,
  component: Watchlist,
};
